import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import SupplierLogo from '@/components/SupplierLogo';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { 
  GlassWater, Croissant, Beef, Droplet, 
  Utensils, Apple, Package, Zap, Cog, TestTube, Microscope, Milk, CupSoda, FileText, Download
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import milkCartonIcon from '@/assets/icons/milk-carton-optimized.webp';
import glassBottleIcon from '@/assets/icons/glass-bottle-optimized.webp';
import spreadsBowlIcon from '@/assets/icons/spreads-bowl-optimized.webp';
import { partnersData } from '@/data/partners';

const iconMap = {
  dairy: milkCartonIcon,
  beverage: glassBottleIcon,
  bakery: Croissant,
  meat: Beef,
  oils: Droplet,
  spreads: spreadsBowlIcon,
  fruit: Apple,
  packaging: Package,
  processing: Cog,
  automation: Cog,
  lab: TestTube,
  energy: Zap
};



const industryFilters = [
  { id: 'dairy', icon: Milk, isImage: false, label: { el: 'Γαλακτοκομικά', en: 'Dairy' } },
  { id: 'beverage', icon: CupSoda, isImage: false, label: { el: 'Ποτά', en: 'Beverages' } },
  { id: 'bakery', icon: Croissant, isImage: false, label: { el: 'Αρτοσκευάσματα, Δημητριακά & Σνακ', en: 'Bakery, Grains & Snacks' } },
  { id: 'meat', icon: Beef, isImage: false, label: { el: 'Κρέας, Ψάρι & Αυγά', en: 'Meat, Fish & Eggs' } },
  { id: 'oils', icon: Droplet, isImage: false, label: { el: 'Έλαια & Λίπη', en: 'Oils & Fats' } },
  { id: 'spreads', icon: Utensils, isImage: false, label: { el: 'Αλείμματα & Σάλτσες', en: 'Spreads & Sauces' } },
  { id: 'fruit', icon: Apple, isImage: false, label: { el: 'Φρούτα & Λαχανικά', en: 'Fruit & Vegetables' } },
  { id: 'ready-meals', icon: Utensils, isImage: false, label: { el: 'Έτοιμα Γεύματα', en: 'Ready Meals' } },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const categoryIconMap: Record<string, any> = {
  dairy: Milk,
  beverage: CupSoda,
  bakery: Croissant,
  meat: Beef,
  oils: Droplet,
  spreads: Utensils,
  fruit: Apple,
  'ready-meals': Package,
};

const Partners = () => {
  const { language, t } = useLanguage();
  const [selectedLetter, setSelectedLetter] = useState<string>('ALL');
  const [selectedIndustry, setSelectedIndustry] = useState<string>('ALL');

  const alphabetFirstRow = 'ABCDEFGHIJKLM'.split('');
  const alphabetSecondRow = 'NOPQRSTUVWXYZ'.split('');
  
  const filteredPartners = partnersData.filter(partner => {
    const letterMatch = selectedLetter === 'ALL' || partner.name.toUpperCase().startsWith(selectedLetter);
    const industryMatch = selectedIndustry === 'ALL' || partner.categoryIcons.includes(selectedIndustry);
    return letterMatch && industryMatch;
  });

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('Συνεργάτες', 'Partners')}
        description={t('Οι στρατηγικοί συνεργάτες της TFT: κορυφαίοι κατασκευαστές εξοπλισμού επεξεργασίας και συσκευασίας τροφίμων παγκοσμίως.', 'TFT strategic partners: leading food processing and packaging equipment manufacturers worldwide.')}
        canonical="https://www.tft.gr/partners"
      />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-b from-primary to-primary-light text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-5xl font-bold mb-6">{t('Συνεργάτες & Προμηθευτές', 'Partners & Suppliers')}</h1>
            <p className="text-xl max-w-3xl mx-auto opacity-90">
              {t('Συνεργαζόμαστε με τους κορυφαίους κατασκευαστές εξοπλισμού παγκοσμίως', 'We collaborate with the world\'s leading equipment manufacturers')}
            </p>
          </div>
        </section>

        {/* Alphabetic Filter */}
        <section className="py-8 bg-muted/30 border-y">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-center gap-2">
              {/* All Button + First Row */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Button
                  variant={selectedLetter === 'ALL' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedLetter('ALL')}
                  className="min-w-[40px] h-10"
                >
                  {t('Όλα', 'All')}
                </Button>
                {alphabetFirstRow.map((letter) => (
                  <Button
                    key={letter}
                    variant={selectedLetter === letter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedLetter(letter)}
                    className="min-w-[40px] h-10"
                  >
                    {letter}
                  </Button>
                ))}
              </div>
              {/* Second Row */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                {alphabetSecondRow.map((letter) => (
                  <Button
                    key={letter}
                    variant={selectedLetter === letter ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedLetter(letter)}
                    className="min-w-[40px] h-10"
                  >
                    {letter}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Industry Filter */}
        <section className="py-6 bg-background border-b">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap items-center justify-center gap-3 mb-3">
              <Button
                variant={selectedIndustry === 'ALL' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedIndustry('ALL')}
                className="h-10"
              >
                {t('Όλες', 'All')}
              </Button>
              {industryFilters.slice(0, 4).map((ind) => {
                const IconComponent = ind.icon as React.ComponentType<{ className?: string }>;
                return (
                  <Button
                    key={ind.id}
                    variant={selectedIndustry === ind.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedIndustry(ind.id)}
                    className="h-10 gap-2"
                  >
                    <IconComponent className="w-4 h-4" />
                    {ind.label[language]}
                  </Button>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {industryFilters.slice(4).map((ind) => {
                const IconComponent = ind.icon as React.ComponentType<{ className?: string }>;
                return (
                  <Button
                    key={ind.id}
                    variant={selectedIndustry === ind.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedIndustry(ind.id)}
                    className="h-10 gap-2"
                  >
                    <IconComponent className="w-4 h-4" />
                    {ind.label[language]}
                  </Button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Partners Grid */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredPartners.map((partner, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4, delay: Math.min(index * 0.05, 0.8) }}
                  whileHover={{ y: -4 }}
                >
                  <Card className="h-full flex flex-col shadow-lg border-none hover:shadow-xl transition-all duration-500 group relative overflow-hidden rounded-xl">
                    {/* Top gradient accent */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0077B6] to-[#003F72] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />
                    {/* Hover gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0077B6]/0 via-[#0077B6]/0 to-[#003F72]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                    <CardContent className="p-6 flex flex-col h-full relative">
                      {/* Logo Stage */}
                      <SupplierLogo 
                        src={partner.logo} 
                        alt={`${partner.name} official logo`}
                        fallbackText={partner.name}
                        size={partner.name === 'Coperion' ? 'large' : 'default'}
                      />
                      
                      {/* Company Name */}
                      <h3 className="text-lg font-semibold mt-4 mb-1 text-[#003F72] group-hover:text-[#0077B6] transition-colors duration-300">
                        {partner.name}
                      </h3>
                      
                      {/* Meta Info */}
                      {partner.founded && partner.hq && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                          <span>{partner.founded}</span>
                          <span>•</span>
                          <span>{partner.hq[language]}</span>
                        </div>
                      )}
                      
                      {/* Description with line clamp */}
                      <p className="text-sm text-muted-foreground mb-3 leading-relaxed line-clamp-3">
                        {partner.description[language]}
                      </p>
                      
                      {/* Strengths (optional) */}
                      {partner.strengths && (
                        <p className="text-xs text-muted-foreground/80 mb-3 italic line-clamp-2">
                          {partner.strengths[language]}
                        </p>
                      )}
                      
                      {/* Category Icons */}
                      {partner.categoryIcons && partner.categoryIcons.length > 0 && (
                        <div className="flex items-center gap-1.5 mb-3">
                          {partner.categoryIcons.map((catId: string) => {
                            const IconComp = categoryIconMap[catId];
                            if (!IconComp) return null;
                            return <IconComp key={catId} className="text-muted-foreground/60" size={15} />;
                          })}
                        </div>
                      )}
                      
                      {/* Spacer to push footer to bottom */}
                      <div className="flex-grow" />
                      
                       {/* Footer Row */}
                      <div className="pt-3 mt-auto border-t flex items-center justify-between gap-2 flex-wrap">
                        {partner.brochures && partner.brochures.length > 0 ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
                                <FileText className="w-3.5 h-3.5" />
                                {t('Τεχνικά Φυλλάδια', 'Technical Brochures')}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64 p-2" align="start">
                              <div className="flex flex-col gap-1">
                                {partner.brochures.map((b, i) => (
                                  <a
                                    key={i}
                                    href={b.url}
                                    className="flex items-center justify-between gap-3 px-3 py-2 rounded hover:bg-muted text-sm text-left"
                                    title={t('Λήψη PDF', 'Download PDF')}
                                  >
                                    <span className="flex items-start gap-2 min-w-0">
                                      <FileText className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                                      <span>{b.label[language]}</span>
                                    </span>
                                    <span className="flex items-center gap-1 text-xs text-primary whitespace-nowrap">
                                      <Download className="w-3.5 h-3.5" />
                                      {t('Λήψη', 'Download')}
                                    </span>
                                  </a>
                                ))}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : <span />}
                        {partner.website && (
                          <a 
                            href={partner.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline"
                            aria-label={`Visit ${partner.name} website`}
                          >
                            {t('Ιστοσελίδα', 'Website')} →
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Partners;
